/**
 * The browser half of "Add to Slack". Before leaving for Slack the tab keeps
 * a pending record: a random `state` and who started the flow. Slack sends
 * the browser back with a one-time code and that state, and the code is used
 * only when the return matches the record. Both records live in the tab's
 * sessionStorage, so no other tab or window can finish this tab's flow,
 * except one opened from this tab, which starts with a copy of its storage.
 */
import type { SlackConnect } from "@/types/alerts";
import { TARGETS_PATH } from "./paths";

export const SLACK_AUTHORIZE_URL = "https://slack.com/oauth/v2/authorize";
export const SLACK_SCOPE = "incoming-webhook";
/** A flow started longer ago than this is refused on return. */
export const PENDING_MAX_AGE_MS = 10 * 60 * 1000;
export const PENDING_CONNECTION_KEY = "kopai.slack-connect.pending";
export const SLACK_RETURN_KEY = "kopai.slack-connect.return";

/** What Slack adds to the URL when it sends the browser back. */
const RETURN_PARAMS = ["code", "state", "error"] as const;
/** The part of a return that must not reach telemetry. */
const SECRET_PARAMS = ["code", "state"] as const;
/** A `redirect` value can be encoded more than once; this many layers are opened. */
const MAX_REDIRECT_DECODES = 3;
/** `code=` or `state=` at the start of a parameter. */
const SECRET_PARAM_PATTERN = /(?:^|[?&#])(?:code|state)=/;

/** The part of `Storage` the records need, so a test can pass its own. */
export type RecordStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export interface PendingConnection {
  state: string;
  userId: string;
  tenantId: string;
  /** Milliseconds since the epoch. */
  startedAt: number;
}

/** A code to exchange, or an error such as `access_denied` on a cancel. */
export type SlackReturn =
  | { code: string; state: string }
  | { error: string; state?: string };

export type SlackReturnRefusal =
  | "no_pending"
  | "state_mismatch"
  | "expired"
  | "identity_changed"
  | "slack_error";

export type SlackReturnVerdict =
  | { kind: "connect"; code: string }
  | { kind: "cancelled" }
  | { kind: "refused"; reason: SlackReturnRefusal };

/** 32 random bytes as hex, so a forged return cannot guess it. */
export function newConnectState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

export function buildSlackAuthorizeUrl(
  connect: SlackConnect,
  state: string,
): string {
  const query = new URLSearchParams({
    client_id: connect.clientId,
    scope: SLACK_SCOPE,
    redirect_uri: connect.redirectUri,
    state,
  });
  return `${SLACK_AUTHORIZE_URL}?${query.toString()}`;
}

/**
 * The return a query string carries, or null when it has neither a code nor
 * an error. A code without a state still counts, so that it is taken out of
 * the URL; the check then refuses it.
 */
export function readSlackReturn(search: string): SlackReturn | null {
  const params = new URLSearchParams(search);
  return toSlackReturn(
    params.get("code"),
    params.get("error"),
    params.get("state"),
  );
}

/** `href` without Slack's return parameters, or null when it had none. */
export function withoutSlackReturn(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  if (!RETURN_PARAMS.some((name) => url.searchParams.has(name))) return null;
  for (const name of RETURN_PARAMS) url.searchParams.delete(name);
  return url.href;
}

/** False when the browser refused to store it. */
export function savePendingConnection(
  storage: RecordStorage,
  pending: PendingConnection,
): boolean {
  return writeRecord(storage, PENDING_CONNECTION_KEY, pending);
}

/**
 * Reads the record and leaves it in place, so a return that is refused
 * cannot end a flow this tab still has open.
 */
export function peekPendingConnection(
  storage: RecordStorage,
): PendingConnection | null {
  return parsePendingConnection(readRecord(storage, PENDING_CONNECTION_KEY));
}

export function clearPendingConnection(storage: RecordStorage): void {
  try {
    storage.removeItem(PENDING_CONNECTION_KEY);
  } catch {
    // Storage refused: there was nothing stored to clear.
  }
}

export function saveSlackReturn(
  storage: RecordStorage,
  ret: SlackReturn,
): void {
  writeRecord(storage, SLACK_RETURN_KEY, ret);
}

/** Reads and removes the record in one call, so it is used at most once. */
export function takeSlackReturn(storage: RecordStorage): SlackReturn | null {
  const value = readRecord(storage, SLACK_RETURN_KEY);
  try {
    storage.removeItem(SLACK_RETURN_KEY);
  } catch {
    // Storage refused: there was nothing stored to remove.
  }
  return parseSlackReturn(value);
}

// Where the browser blocks site data: reads find nothing and writes fail,
// so a flow is never started that could not be finished.
const NO_STORAGE: RecordStorage = {
  getItem: () => null,
  setItem: () => {
    throw new Error("This browser blocks site data");
  },
  removeItem: () => {},
};

/** The tab's sessionStorage; reading the property throws where site data is blocked. */
export function tabStorage(): RecordStorage {
  try {
    return window.sessionStorage;
  } catch {
    return NO_STORAGE;
  }
}

/**
 * What a return from Slack means for this tab. A code is used only when this
 * tab started the flow (same state), less than ten minutes ago, as the user
 * and workspace signed in now. A cancel ends the flow, so it too must carry
 * this tab's state.
 */
export function checkSlackReturn(
  pending: PendingConnection | null,
  ret: SlackReturn,
  identity: { userId: string | null; tenantId: string | null },
  now: number,
): SlackReturnVerdict {
  if ("error" in ret && ret.error !== "access_denied") {
    return refused("slack_error");
  }
  if (!pending) return refused("no_pending");
  if (ret.state !== pending.state) return refused("state_mismatch");
  if ("error" in ret) return { kind: "cancelled" };
  if (now - pending.startedAt > PENDING_MAX_AGE_MS) return refused("expired");
  if (
    pending.userId !== identity.userId ||
    pending.tenantId !== identity.tenantId
  ) {
    return refused("identity_changed");
  }
  return { kind: "connect", code: ret.code };
}

/**
 * `href` without `code` and `state` values: in the query, in a fragment
 * written like a query, and inside every `redirect` parameter, where the
 * login page keeps the path to come back to. Returned as the same string
 * when there is nothing to remove or it is not a URL.
 */
export function scrubSlackReturn(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const queryChanged = scrubParams(url.searchParams);
  const fragment = url.hash.slice(1);
  const scrubbedFragment = scrubFragment(fragment);
  if (!queryChanged && scrubbedFragment === fragment) return href;
  if (scrubbedFragment !== fragment) url.hash = scrubbedFragment;
  return url.href;
}

/**
 * Takes a Slack code out of the address bar before anything else reads it.
 * On the targets page the return moves into the tab's storage, where the
 * page picks it up. On any other page only the login page's `redirect`
 * parameter is touched, where a return can wait while the user signs in; a
 * `code` that belongs to another flow, such as a sign-in, is never removed.
 * Reads `window` on purpose: it runs once, at startup.
 */
export function stashSlackReturnFromUrl(): void {
  const { href, pathname, search } = window.location;
  let clean = href;
  if (pathname === TARGETS_PATH) {
    const ret = readSlackReturn(search);
    if (ret) saveSlackReturn(tabStorage(), ret);
    clean = withoutSlackReturn(href) ?? href;
  } else {
    clean = withoutRedirectedReturn(href);
  }
  if (clean !== href) {
    window.history.replaceState(window.history.state, "", clean);
  }
}

/** `href` with a Slack return removed from its `redirect` parameters only. */
function withoutRedirectedReturn(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  return scrubRedirectParams(url.searchParams) ? url.href : href;
}

function refused(reason: SlackReturnRefusal): SlackReturnVerdict {
  return { kind: "refused", reason };
}

/** An error wins over a code: Slack sends a code only when the admin allowed. */
function toSlackReturn(
  code: unknown,
  error: unknown,
  state: unknown,
): SlackReturn | null {
  const stateText = typeof state === "string" ? state : undefined;
  if (typeof error === "string" && error !== "") {
    return stateText === undefined ? { error } : { error, state: stateText };
  }
  if (typeof code === "string" && code !== "") {
    return { code, state: stateText ?? "" };
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parsePendingConnection(value: unknown): PendingConnection | null {
  if (!isRecord(value)) return null;
  const { state, userId, tenantId, startedAt } = value;
  if (
    typeof state !== "string" ||
    state === "" ||
    typeof userId !== "string" ||
    typeof tenantId !== "string" ||
    typeof startedAt !== "number" ||
    !Number.isFinite(startedAt)
  ) {
    return null;
  }
  return { state, userId, tenantId, startedAt };
}

function parseSlackReturn(value: unknown): SlackReturn | null {
  if (!isRecord(value)) return null;
  const { code, error, state } = value;
  if (state !== undefined && typeof state !== "string") return null;
  return toSlackReturn(code, error, state);
}

function writeRecord(
  storage: RecordStorage,
  key: string,
  value: unknown,
): boolean {
  try {
    storage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

function readRecord(storage: RecordStorage, key: string): unknown {
  try {
    const raw = storage.getItem(key);
    return raw === null ? null : JSON.parse(raw);
  } catch {
    // Unreadable storage or an unparseable value reads as nothing stored.
    return null;
  }
}

/** Removes the secret parameters in place; true when anything changed. */
function scrubParams(params: URLSearchParams): boolean {
  let changed = false;
  for (const name of SECRET_PARAMS) {
    if (params.has(name)) {
      params.delete(name);
      changed = true;
    }
  }
  return scrubRedirectParams(params) || changed;
}

/** Scrubs every `redirect` value in place; true when any of them changed. */
function scrubRedirectParams(params: URLSearchParams): boolean {
  const redirects = params.getAll("redirect");
  const scrubbed = redirects.map(scrubRedirect);
  if (!scrubbed.some((value, i) => value !== redirects[i])) return false;
  params.delete("redirect");
  for (const value of scrubbed) params.append("redirect", value);
  return true;
}

/**
 * A `redirect` value without its secrets. A value encoded again on the way
 * is opened layer by layer until the secrets show, and the scrubbed result
 * goes back encoded once. Unchanged when there is nothing to remove.
 */
function scrubRedirect(value: string): string {
  let layer = value;
  for (let decodes = 0; decodes <= MAX_REDIRECT_DECODES; decodes++) {
    if (SECRET_PARAM_PATTERN.test(layer)) {
      const scrubbed = scrubPathQuery(layer);
      return scrubbed === layer ? value : scrubbed;
    }
    const next = decodeOnce(layer);
    if (next === layer) break;
    layer = next;
  }
  return value;
}

function decodeOnce(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    // A stray `%` that is not an escape: there is no further layer.
    return text;
  }
}

/** The same for a path such as `/alerts/targets?code=…`, which `URL` cannot parse alone. */
function scrubPathQuery(path: string): string {
  const hashAt = path.indexOf("#");
  const beforeHash = hashAt === -1 ? path : path.slice(0, hashAt);
  const fragment = hashAt === -1 ? null : path.slice(hashAt + 1);
  const queryAt = beforeHash.indexOf("?");
  const pathOnly = queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt);
  const query = queryAt === -1 ? null : beforeHash.slice(queryAt + 1);

  const scrubbedQuery = query === null ? null : scrubQueryText(query);
  const scrubbedFragment = fragment === null ? null : scrubFragment(fragment);
  if (scrubbedQuery === query && scrubbedFragment === fragment) return path;
  return `${pathOnly}${scrubbedQuery ? `?${scrubbedQuery}` : ""}${
    scrubbedFragment ? `#${scrubbedFragment}` : ""
  }`;
}

function scrubQueryText(query: string): string {
  const params = new URLSearchParams(query);
  return scrubParams(params) ? params.toString() : query;
}

/** Only a fragment written like a query (`code=…&state=…`) can carry them. */
function scrubFragment(fragment: string): string {
  return fragment.includes("=") ? scrubQueryText(fragment) : fragment;
}
